import { useNavigate } from "react-router-dom";
import { Layout, Button, SectionLabel, SectionHeading, PageHero } from "../components.jsx";
import { navy, blue, SERVICES, AI_SYSTEMS, RESULTS } from "../data";

const ENGAGEMENT = [
  { tier:"Tier 1", title:"AI Opportunity Assessment", price:"$2,000 – $5,000",   items:["Business workflow review","AI opportunity map","Automation roadmap","ROI projection"] },
  { tier:"Tier 2", title:"AI Automation Build",       price:"$10,000 – $40,000", items:["Workflow automation build","CRM & tool integrations","AI assistant / chatbot","Full deployment & training"], featured:true },
  { tier:"Tier 3", title:"AI Transformation Program", price:"$50,000 – $150,000+",items:["Multiple AI systems","Data intelligence layer","Full automation infrastructure","Ongoing optimisation"] },
];

export default function Services() {
  const navigate = useNavigate();

  return (
    <Layout>
      <PageHero
        label="What We Do"
        title="AI Services Built Around Your Operations"
        subtitle="We don't just build AI tools — we solve real operational problems with practical, deployable systems that deliver measurable business outcomes."
      />

      {/* ── SERVICES GRID ───────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Our Services</SectionLabel>
            <SectionHeading>Six Ways We Help Your Business</SectionHeading>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map(s => (
              <div key={s.title} className="rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 group bg-white">
                <div className="text-3xl mb-5">{s.icon}</div>
                <h3 className="font-bold text-base mb-3 leading-snug group-hover:text-blue-600 transition-colors" style={{ color: navy }}>{s.title}</h3>
                <p className="text-gray-400 text-sm mb-3 leading-relaxed"><span className="font-semibold text-gray-500">Problem: </span>{s.problem}</p>
                <p className="text-blue-600 text-sm font-semibold leading-relaxed">✓ {s.outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BEFORE / AFTER ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>The Transformation</SectionLabel>
            <SectionHeading>What Operations Look Like Before and After AI</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              AI automation doesn't just save time — it changes how your entire operation runs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-lg p-8 border-2 border-red-100 bg-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-500 font-bold text-sm">✕</div>
                <h3 className="font-bold text-lg" style={{ color: navy }}>Before AI</h3>
              </div>
              <div className="space-y-4">
                {["Leads sit in inboxes for hours before anyone responds","Teams manually schedule appointments and follow up on enquiries","Reports built in spreadsheets every week — always out of date","Important business knowledge lives in people's heads","Customer enquiries overwhelm staff during busy periods"].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="text-red-400 flex-shrink-0 mt-0.5 font-bold">✕</span>
                    <p className="text-gray-600 text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg p-8 border-2 border-blue-100" style={{ background: "#f0f7ff" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">✓</div>
                <h3 className="font-bold text-lg" style={{ color: navy }}>After AI</h3>
              </div>
              <div className="space-y-4">
                {["Leads captured, qualified, and followed up automatically — 24/7","AI assistants respond instantly to common enquiries and book appointments","Scheduling and reminders fully automated — no manual coordination","Real-time dashboards replace manual reports and guesswork","Teams redirected to higher-value work while AI handles the rest"].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="text-blue-500 flex-shrink-0 mt-0.5 font-bold">✓</span>
                    <p className="text-gray-700 text-sm leading-relaxed font-medium">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI SYSTEMS ──────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: navy }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>In Practice</SectionLabel>
            <SectionHeading light>Example AI Systems We Build</SectionHeading>
            <p className="text-gray-400 mt-4 max-w-xl mx-auto text-base">Practical, deployable systems built around real business operations — not generic software demos.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {AI_SYSTEMS.map(sys => (
              <div key={sys.title} className="rounded-lg p-7 border border-white/10 hover:border-blue-500/40 transition-all duration-200" style={{ background: "rgba(255,255,255,0.04)" }}>
                <div className="text-3xl mb-4">{sys.icon}</div>
                <h3 className="text-white font-bold text-lg mb-2">{sys.title}</h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed"><span className="text-gray-300 font-semibold">Problem: </span>{sys.problem}</p>
                <div className="rounded p-3 mb-4 border border-white/10" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-1">How It Works</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{sys.flow}</p>
                </div>
                <p className="text-blue-400 text-sm font-semibold">✓ {sys.outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW AI PROJECTS WORK ────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Our Process</SectionLabel>
            <SectionHeading>How AI Automation Projects Work</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              A structured approach from identifying opportunities to deploying AI systems that run inside your business operations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Discovery & Workflow Review", desc: "We review your operations, tools, and workflows to identify where automation will create the most impact." },
              { num: "02", title: "AI Automation Architecture", desc: "We design the AI systems and integrations needed to automate key processes across your business." },
              { num: "03", title: "System Development & Integration", desc: "We build and deploy AI systems integrated with your CRM, communication tools, and operational platforms." },
              { num: "04", title: "Deployment & Optimisation", desc: "Your systems go live, your team is trained, and we refine performance as the systems scale." },
            ].map((step, i, arr) => (
              <div key={step.num} className="relative rounded-lg p-7 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 bg-white">
                <div className="text-5xl font-bold mb-4 opacity-10" style={{ color: navy }}>{step.num}</div>
                <div className="w-8 h-0.5 mb-4" style={{ background: "#2563eb" }} />
                <h3 className="font-bold text-base mb-3 leading-snug" style={{ color: navy }}>{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                {i < arr.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 text-blue-400 text-lg font-bold">→</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RESULTS ─────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Outcomes</SectionLabel>
            <SectionHeading>What Our Clients Gain</SectionHeading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {RESULTS.map(r => (
              <div key={r.label} className="bg-white rounded-lg p-7 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="text-3xl mb-4">{r.icon}</div>
                <div className="text-2xl font-bold mb-1" style={{ color: navy }}>{r.metric}</div>
                <div className="text-blue-600 font-semibold text-sm mb-3">{r.label}</div>
                <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENGAGEMENT TIERS ────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <SectionLabel>Engagement Models</SectionLabel>
            <SectionHeading>How We Work Together</SectionHeading>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">Three structured engagement tiers — from an initial assessment to a full AI transformation programme.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ENGAGEMENT.map(e => (
              <div
                key={e.tier}
                className="rounded-lg p-8 border-2 transition-all duration-200"
                style={{
                  borderColor: e.featured ? blue : "#e5e7eb",
                  background: e.featured ? navy : "#fff",
                }}
              >
                <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: e.featured ? "#60a5fa" : blue }}>{e.tier}</p>
                <h3 className="font-bold text-xl mb-2" style={{ color: e.featured ? "#fff" : navy }}>{e.title}</h3>
                <p className="font-bold text-lg mb-5" style={{ color: e.featured ? "#60a5fa" : blue }}>{e.price}</p>
                <ul className="space-y-2 mb-6">
                  {e.items.map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm" style={{ color: e.featured ? "#d1d5db" : "#374151" }}>
                      <span className="flex-shrink-0" style={{ color: "#60a5fa" }}>✓</span>{item}
                    </li>
                  ))}
                </ul>
                <Button
                  variant={e.featured ? "primary" : "outline"}
                  className="w-full justify-center"
                  onClick={() => navigate("/contact")}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────────── */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <SectionLabel>Get Started</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Automate Your Operations?</h2>
          <p className="text-gray-300 text-base mb-10 max-w-xl mx-auto leading-relaxed">Book a free strategy call and we'll identify where AI can deliver the most value in your business — no obligation.</p>
          <Button onClick={() => navigate("/contact")}>Book Your Free Strategy Call</Button>
        </div>
      </section>

    </Layout>
  );
}